import { gql, useApolloClient } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useAppSelector } from "../../../../Store";
import { getFarmSettingsState } from "../../UserCalls/dataCalls";
import { DateTime } from "luxon";
import { LocaleFarmDate } from "../../../../Definitions";
import { useTranslation } from "react-i18next";

export const GET_NEXT_FEEDING_TIME_INFORMATION = gql`
	query NextFeedingTimeInformation($domain: String!) {
		animal {
			nextFeedingTimeInformation(domain: $domain) {
				next_time
				animal_ids
			}
		}
	}
`

export interface NextFeedingTimeInformation {
	next_time?: number
	animal_ids: number[]
}

export interface NextFeedingTimeInformationResponse {
	animal: {
		nextFeedingTimeInformation: NextFeedingTimeInformation
	}
}

export function useGetNextFeedingTimeInformation() {
	const { t } = useTranslation();
	const client = useApolloClient();
	const [data, setData] = useState<NextFeedingTimeInformation>({ next_time: undefined, animal_ids: [] });
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);

	const farmSettings = useAppSelector(getFarmSettingsState);

	const timezoneOffset = useMemo(() => {
		const timezoneOffset = DateTime.now().setZone(farmSettings?.time_zone_label);
		return timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0);
	}, [farmSettings?.time_zone_label]);

	const getNextFeedingTimeInformation = useCallback(async (domain: string) => {
		setLoading(true);
		setError(undefined);

		try {
			const result = await client.query<NextFeedingTimeInformationResponse>({query: GET_NEXT_FEEDING_TIME_INFORMATION, variables: { domain }, fetchPolicy:'no-cache'});
			const res = result.data.animal.nextFeedingTimeInformation;
			if (!res) {
				throw new Error(t("noData", { defaultValue: "no data" }));
			}

			setData({next_time: res.next_time ? LocaleFarmDate.restrictTimeTo24Hours(res.next_time + timezoneOffset) : undefined, animal_ids: res.animal_ids});
		} catch (error) {
			setData({ next_time: undefined, animal_ids: [] });
			setError("Error");
			console.error(error);
		} finally {
			setLoading(false);
		}
	}, [timezoneOffset, client]);

	return { data, error, loading, getNextFeedingTimeInformation };
}